import { ChipOwnProps } from '@mui/material';
import { z } from 'zod/v4-mini';

import {
  TelemonitoringTags,
  telemonitoringTagsSchema,
} from '@/models/CarePlanModel.ts';
import {
  ConditionType,
  MedicationSchema,
  conditionTypeSchema,
  medicationSchemeSchema,
} from '@/models/ConditionModel.ts';
import {
  UnreadMessageCount,
  unreadMessageCountSchema,
} from '@/models/MessagingModel.ts';
import {
  PractitionerSummary,
  practitionerSummarySchema,
} from '@/models/PractitionerModel.ts';
import { Schema, cz } from '@/utils/zodUtils.ts';

export const renewalSuggestionSchema = z.literal(['expiring', 'add_insulin']);
export type RenewalSuggestion = z.infer<typeof renewalSuggestionSchema>;

export type PatientListItem = {
  id: string;
  givenName: string;
  familyName: string;

  doctor?: PractitionerSummary;
  unreadMessages: UnreadMessageCount;
  diabetesType: ConditionType;
  insulinTreatmentType: MedicationSchema;
  renewalSuggestion?: RenewalSuggestion;
  hasNurseNotice: boolean;

  tags: TelemonitoringTags[];
};

export type PatientList = PatientListItem[];

export const patientListItemSchema: Schema<PatientListItem> = cz.transform(
  cz.camelCaseObject({
    id: z.string(),
    given_name: z.string(),
    family_name: z.string(),
    doctor: cz.optional(practitionerSummarySchema),
    unread_messages: unreadMessageCountSchema,
    renewal_suggestion: cz.optional(renewalSuggestionSchema),
    has_nurse_notice: z.boolean(),
    diabetes_type: conditionTypeSchema,
    insulin_treatment_type: medicationSchemeSchema,
    telemonitoring_tags: cz.forcedArray(telemonitoringTagsSchema),
  }),
  data => ({ ...data, tags: data.telemonitoringTags }),
);

export const telemonitoringTagColorMapping = (
  tag: TelemonitoringTags,
): ChipOwnProps['color'] => {
  switch (tag) {
    case 'active':
    case 'gestational_no_insulin':
      return 'green';
    case 'expiring_soon':
      return 'neutral';
    case 'expired':
    case 'expired_recently':
      return 'red';
    case 'no_glycemia_data':
      return 'purple';
    case 'nurse_asked_for_reinclusion':
      return 'blue';
  }
};

export const telemonitoringTagTranslationMapping = (
  tag: TelemonitoringTags,
): string => {
  switch (tag) {
    case 'active':
      return 'models.telemonitoringTag.active';
    case 'expiring_soon':
      return 'models.telemonitoringTag.expiringSoon';
    case 'expired':
      return 'models.telemonitoringTag.expired';
    case 'expired_recently':
      return 'models.telemonitoringTag.expiredRecently';
    case 'gestational_no_insulin':
      return 'models.telemonitoringTag.gestationalNoInsulin';
    case 'no_glycemia_data':
      return 'models.telemonitoringTag.onboarding';
    case 'nurse_asked_for_reinclusion':
      return 'models.telemonitoringTag.nurseAskedForReinclusion';
  }
};
